/**
 * BillingNotesDialog — R148g · Feb 2026
 * -------------------------------------
 * Private note thread between PMs and Accounts, scoped to a specific
 * task OR project. Only accessed from the Tasks → Billing subtab so
 * the customer never sees these notes.
 *
 * Usage:
 *   <BillingNotesDialog
 *     wsId={wsId}
 *     scope="task"       // or "project"
 *     scopeId={t.id}
 *     scopeLabel={t.title}
 *     onClose={() => setOpen(null)}
 *     onChange={() => refresh()}
 *   />
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Loader2, User, Info } from "lucide-react";

export default function BillingNotesDialog({ wsId, scope, scopeId, scopeLabel, onClose, onChange }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    if (!wsId || !scopeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/billing-notes`, {
        params: { scope, scope_id: scopeId },
      });
      setNotes(data.notes || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load notes")); }
    finally { setLoading(false); }
  }, [wsId, scope, scopeId]);

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    const b = body.trim();
    if (!b) return;
    setPosting(true);
    try {
      await api.post(`/workspaces/${wsId}/billing-notes`, {
        scope, scope_id: scopeId, body: b,
      });
      setBody("");
      await load();
      onChange?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Post failed")); }
    finally { setPosting(false); }
  };

  const handleKey = (e) => {
    // Cmd/Ctrl+Enter to post — matches Slack / Notion muscle memory.
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      post();
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto rounded-none z-[130]"
        data-testid="billing-notes-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Accounts notes · <span className="font-normal truncate max-w-[40ch]">{scopeLabel}</span>
          </DialogTitle>
          <DialogDescription>
            Private thread between PMs and Accounts — <b>only visible from the Billing tab</b>. The customer never sees these notes.
          </DialogDescription>
        </DialogHeader>

        {/* Thread */}
        <div className="border border-border max-h-72 overflow-y-auto divide-y divide-border" data-testid="billing-notes-thread">
          {loading ? (
            <div className="p-4 text-xs text-muted-foreground text-center">Loading…</div>
          ) : notes.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground text-center flex flex-col items-center gap-1">
              <Info className="w-4 h-4 opacity-40" />
              No notes yet — post the first one below.
            </div>
          ) : notes.map((n) => (
            <div key={n.id} className="px-3 py-2" data-testid={`billing-note-${n.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <User className="w-3 h-3 text-muted-foreground" />
                <span className="text-[11px] font-semibold">{n.author_name}</span>
                <span className="text-[10px] text-muted-foreground">{fmtRelative(n.created_at)}</span>
              </div>
              <div className="text-xs whitespace-pre-wrap break-words">{n.body}</div>
            </div>
          ))}
        </div>

        {/* Composer */}
        <div className="space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Add a billing note… (⌘/Ctrl+Enter to post)"
            rows={3}
            className="rounded-none text-sm"
            data-testid="billing-notes-composer"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground">
              {notes.length} note{notes.length === 1 ? "" : "s"} · Only PMs + Accounts see this.
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="rounded-none" onClick={onClose}>Close</Button>
              <Button
                size="sm"
                className="rounded-none"
                onClick={post}
                disabled={posting || !body.trim()}
                data-testid="billing-notes-post"
              >
                {posting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Post note
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
